import { Link } from "react-router-dom";
import React, { useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Layout from "../../../components/layout/Layout.js";

import {
  useSyncConfigs,
  useDeleteSyncConfig,
  useRunSync,
  useSyncRuns,
} from "../../../api/queries/adminQueries";
import { useCompanies } from "../../../api/queries/organizationQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../../utils/apiError";
import SyncConfigDialog from "./SyncConfigDialog";
import SyncTaskLogs from "./SyncTaskLogs";
import { formatCronLabel, RUN_STATUS, runProblems } from "./syncFormat";
import "./DataSync.css";
import { useCanModify } from "../../../hooks/usePermissions";

// Activate the relativeTime plugin
dayjs.extend(relativeTime);

const LATEST_RUN = { limit: 1, skip: 0 };
const POLL_AFTER_RUN_MS = 60 * 1000;

/** The task's latest logged run, so failures are visible without opening the log. */
const LastResult = ({ config, pollUntil }) => {
  const query = useSyncRuns(config.id, LATEST_RUN, { pollUntil });
  const run = query.data?.data?.[0];
  const when = config.lastRunAt ? dayjs(config.lastRunAt).fromNow() : "Never";
  if (!run) return when;
  const status = RUN_STATUS[run.status];
  return (
    <>
      <div>{when}</div>
      <div className="d-flex flex-wrap justify-content-center gap-1 mt-1">
        <span className={`status-pill status-pill--${status?.tone || "gray"}`}>
          {status?.label || run.status}
        </span>
        {runProblems(run).map((p) => (
          <span key={p.text} className={`status-pill status-pill--${p.tone}`}>
            {p.text}
          </span>
        ))}
      </div>
      <div className="fs-070 mt-1">
        {run.linked ?? 0} of {run.totalFiles ?? 0} linked
      </div>
    </>
  );
};

const ScheduleSync = () => {
  // View-only roles see the list without create / edit / delete
  const canModify = useCanModify("data-tools", { company: true });
  const { data: configs = [] } = useSyncConfigs();
  const deleteSync = useDeleteSyncConfig();
  const runSync = useRunSync();
  const user = useCurrentUser();
  const companies = useCompanies().data || [];
  const companyNameOf = (id) =>
    companies.find((c) => c.id === id)?.companyName || "Unknown company";
  // Several configs can run at once, so track "running" per config id.
  const [syncingIds, setSyncingIds] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  // { config } while the form is open; config is null for a new task
  const [form, setForm] = useState(null);
  const [logsFor, setLogsFor] = useState(null); // config whose log is open
  const [pollUntil, setPollUntil] = useState({}); // { [id]: time }, after Run Now

  const handleRunTask = async (config) => {
    const { id } = config;
    try {
      setSyncingIds((prev) => ({ ...prev, [id]: "running" }));
      const result = await runSync.mutateAsync(id);
      // It carries on in the background: the log shows how it goes
      if (result.started === false) toast.info(result.message);
      else toast.success(result.message || "Sync started.");
      setPollUntil((prev) => ({
        ...prev,
        [id]: Date.now() + POLL_AFTER_RUN_MS,
      }));
      setLogsFor(config);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Manual sync failed"));
    } finally {
      setSyncingIds(({ [id]: _done, ...rest }) => rest);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Permanently delete this configuration?")) {
      try {
        await deleteSync.mutateAsync(id);
        toast.info("Config removed");
      } catch (err) {
        toast.error(getApiErrorMessage(err, "Failed to delete configuration"));
      }
    }
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Sync Schedules</h1>
            <p className="text-muted small mb-0">
              Scheduled syncs from S3 / Zoho.
            </p>
          </div>
          {canModify && (
            <button
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={() => setForm({ config: null })}
            >
              <i className="bi bi-plus-lg me-2"></i>New Task
            </button>
          )}
        </div>

        <div className="mb-4">
          <input
            type="text"
            className="form-control border-0 shadow-sm px-4 rounded-10 h-45px"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="glass-container overflow-hidden shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3">Config Details</th>
                <th className="py-3 text-center">Schedule</th>
                <th className="py-3 text-center">Last Run</th>
                <th className="pe-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs
                .filter((c) =>
                  c.name.toLowerCase().includes(searchTerm.toLowerCase()),
                )
                .map((config) => (
                  <tr key={config.id} className="align-middle">
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark fs-085">
                        {config.name}
                      </div>
                      <div className="text-muted fs-075">
                        {companyNameOf(config.companyId)}
                      </div>
                      <div className="text-muted font-monospace fs-065">
                        ID: {config.id}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="schedule-badge">
                        <i className="bi bi-calendar-event me-2"></i>
                        {formatCronLabel(config.cronExpression)}
                      </div>
                    </td>
                    <td className="text-center text-muted fs-075">
                      <LastResult
                        config={config}
                        pollUntil={pollUntil[config.id]}
                      />
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setLogsFor(config)}
                        >
                          <i className="bi bi-journal-text me-1"></i>Logs
                        </button>
                        {canModify && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-dark"
                              onClick={() => setForm({ config })}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleRunTask(config)}
                              disabled={syncingIds[config.id] === "running"}
                            >
                              {syncingIds[config.id] === "running"
                                ? "Starting..."
                                : "Run Now"}
                            </button>
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() => handleDelete(config.id)}
                              aria-label={`Delete ${config.name}`}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <SyncConfigDialog
          config={form.config}
          defaultCompanyId={user.companyId}
          onClose={() => setForm(null)}
        />
      )}
      {logsFor && (
        <SyncTaskLogs
          config={logsFor}
          pollUntil={pollUntil[logsFor.id]}
          onClose={() => setLogsFor(null)}
        />
      )}
    </Layout>
  );
};

export default ScheduleSync;
